import type { KeyboardEvent, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { Activity } from '@models/activity.model';
import { Incident } from '@models/incident.model';
import { ActivityHeader } from '../ActivityHeader';
import { formatActivityTime } from '@utils/formatDate';
import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import { cn } from '@utils/cn';
import { TaskName } from '../TaskName';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import {
  getFileIcon,
  getFileType,
  getTypeColor,
  isImageFile,
} from '@utils/documentUtils';
import { useTranslation } from '@hooks/useTranslation';

export const ActivityIncident = ({
  activity,
  handleOpenTaskDetail,
  onIncidentAcknowledge,
}: {
  activity: Activity;
  handleOpenTaskDetail?: (taskId: string) => void;
  onIncidentAcknowledge?: (incident: Incident) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const tIncidents = useTranslation('incidents');
  const incident = activity.incident;
  const history = useHistory();

  // Sprint 16, story-076 (GRpkXE4U). This card had no way to reach the incident's own detail
  // view — clicking anywhere on it (outside the Acknowledge controls and the "Affected Tasks"
  // link) now navigates to `/incidents/:id`. Guarded on `incidentId` being present.
  const handleCardClick = () => {
    if (incident?.incidentId) {
      history.push(`/incidents/${incident.incidentId}`);
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent) => {
    // Sprint 16, develop run 2 (fix for regression found during review). Only act when the key
    // event originated on the card element itself. Interactive descendants (e.g. the Acknowledge
    // button) still bubble keydown up to the card even though their own click handlers stop
    // propagation, since `stopPropagation` on click does nothing for a separate keydown event.
    // Without this guard, pressing Enter/Space on the Acknowledge button both fires the button's
    // native click AND this handler's preventDefault+navigate, sending the user to the incident
    // detail page instead of acknowledging.
    if (event.target !== event.currentTarget) return;
    if (!incident?.incidentId) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  // Enhanced acknowledgment detection - focus ONLY on THIS activity, not the incident's current status
  const isAcknowledgment =
    // Check activity type
    activity.type === 'Incident Acknowledgment' ||
    activity.type === 'INCIDENT_ACKNOWLEDGED' ||
    activity.type === 'INCIDENT_ACKNOWLEDGMENT' ||
    // Check activity description and name patterns (primary indicators)
    activity.description?.toLowerCase().includes('was acknowledged') ||
    activity.name?.toLowerCase().includes('acknowledged');
  // NOTE: Removed incident status checks because they reflect current state,
  // not what THIS specific activity represents

  const projectName =
    incident?.project?.name || activity.project?.name || tActivityCards.notAvailable;

  const canAcknowledge =
    !isAcknowledgment &&
    incident?.status === 'PENDING' &&
    onIncidentAcknowledge;

  // Sprint 28 change request (story-144 History, defect fix). Was rendering `incident.status`
  // (the raw `IncidentStatus` enum value) directly — a Spanish user saw the English word
  // "PENDING"/"ACKNOWLEDGED". `IncidentStatus` only has these two values
  // (backend/src/entities/incident/incident.entity.ts), so a small local map is enough; no need
  // for a non-component module per Decision 2, since this is already a component.
  const incidentStatusTextMap: Record<string, string> = {
    PENDING: tActivityCards.incident.statusPending,
    ACKNOWLEDGED: tActivityCards.incident.statusAcknowledged,
  };

  // Sprint 28 change request (sixth-site defect fix, orchestrator-confirmed). Was rendering
  // `incident.incidentType.replace(/_/g, ' ')` and `incident.delayUnitOfMeasure?.toLowerCase()`
  // directly — a Spanish user saw raw English ("MATERIALS ISSUE", "hours"). Reuses the
  // already-shipped `incidents.type*`/`incidents.unit*` keys (built for ReportIncidentModal)
  // instead of introducing a second set of keys for the same concepts. Unmapped values fall back
  // to the raw value, never `undefined`; `OTHER` + `otherIncidentType` stays untranslated
  // user-entered content, unchanged.
  const incidentTypeTextMap: Record<string, string> = {
    WEATHER: tIncidents.typeWeather,
    ACCIDENT: tIncidents.typeAccident,
    MATERIALS_ISSUE: tIncidents.typeMaterialsIssue,
    EQUIPMENT_ISSUE: tIncidents.typeEquipmentIssue,
    OTHER: tIncidents.typeOther,
  };
  const delayUnitTextMap: Record<string, string> = {
    HOURS: tIncidents.unitHours,
    DAYS: tIncidents.unitDays,
    WEEKS: tIncidents.unitWeeks,
  };
  const getIncidentTypeDisplay = (type?: string, otherType?: string) => {
    if (!type) return '';
    if (type === 'OTHER' && otherType) return otherType;
    return incidentTypeTextMap[type] || tActivityCards.incident.unknownTypeFallback;
  };
  const getDelayUnitDisplay = (unit?: string) => {
    if (!unit) return '';
    return delayUnitTextMap[unit] || unit.toLowerCase();
  };

  // Sprint 16, story-076: MUST stop propagation now that the card itself has a click handler —
  // neither of these two call sites did before this sprint (verified directly against the code;
  // a plain `<span onClick>`/`<button onClick>` nested inside a card-level `onClick` div still
  // bubbles), so without this, acknowledging would also navigate away mid-click.
  const handleAcknowledgeClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (incident && onIncidentAcknowledge) {
      onIncidentAcknowledge(incident);
    }
  };

  // Check if attemptAt exists and is different from createdAt (indicates offline creation)
  const hasAttemptedAt =
    activity.attemptAt &&
    activity.createdAt &&
    new Date(activity.attemptAt).getTime() !==
      new Date(activity.createdAt).getTime();

  const rightLabel = (
    <div className="flex flex-col items-end gap-0.5">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 text-sm font-medium ${
            isAcknowledgment ? 'text-green-600' : 'text-red-600'
          }`}>
          {isAcknowledgment ? (
            <>
              <CheckCircleOutlinedIcon className="w-4 h-4" />
              {tActivityCards.incident.incidentAcknowledged}
            </>
          ) : (
            <>
              <ReportProblemOutlinedIcon className="w-4 h-4" />
              {tActivityCards.incident.incidentReported}
            </>
          )}
        </span>
        <p className="text-sm text-neutral-500">
          {`- ${formatActivityTime(activity.createdAt)}`}
        </p>
      </div>
      {!isAcknowledgment && hasAttemptedAt && (
        <p className="text-[12px] text-neutral-400">
          {tActivityCards.incident.attemptedAt.replace(
            '{time}',
            formatActivityTime(activity.attemptAt),
          )}
        </p>
      )}
    </div>
  );

  const canNavigate = !!incident?.incidentId;

  return (
    <Card
      className={cn(canNavigate && 'cursor-pointer hover:ring-1 hover:ring-indigo-100')}
      onClick={canNavigate ? handleCardClick : undefined}
      onKeyDown={canNavigate ? handleCardKeyDown : undefined}
      role={canNavigate ? 'button' : undefined}
      tabIndex={canNavigate ? 0 : undefined}>
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="mt-4 space-y-3">
        <Alert>
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              <span className="font-medium text-gray-900">{tActivityCards.incident.incidentLabel}</span>{' '}
              {incident?.name || activity.name || tActivityCards.notAvailable}
            </p>
            <p>
              <span className="font-medium text-gray-900">{tActivityCards.projectLabel}</span>{' '}
              {projectName}
            </p>
            {incident?.tasks && incident.tasks.length > 0 && (
              <TaskName
                size="sm"
                label={tActivityCards.incident.affectedTasksLabel}
                task={incident.tasks[0]}
                taskId={incident.tasks[0].taskId}
                onOpenTask={handleOpenTaskDetail}
              />
            )}
          </div>
        </Alert>

        {isAcknowledgment ? (
          <Alert variant="success" border>
            <div className="text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                  <CheckCircleOutlinedIcon className="w-4 h-4" />
                  {tActivityCards.incident.acknowledgedLabel}
                </span>
                {incident?.acknowledgedAt && (
                  <span className="text-gray-600 text-xs">
                    {formatActivityTime(incident.acknowledgedAt)}
                  </span>
                )}
              </div>
              {incident?.acknowledgedByUser && (
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">
                    {tActivityCards.incident.acknowledgedByLabel}
                  </span>{' '}
                  {incident.acknowledgedByUser.firstName}{' '}
                  {incident.acknowledgedByUser.lastName}
                </p>
              )}
              <p className="text-gray-700">
                <span className="font-medium text-gray-900">
                  {tActivityCards.incident.originalIncidentLabel}
                </span>{' '}
                {incident?.name || tActivityCards.notAvailable}
              </p>
              {incident?.incidentType && (
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">{tActivityCards.incident.typeLabel}</span>{' '}
                  {getIncidentTypeDisplay(incident.incidentType, incident.otherIncidentType)}
                </p>
              )}
            </div>
          </Alert>
        ) : (
          <Alert variant="error" border>
            <div className="text-sm space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      canAcknowledge
                        ? 'bg-amber-100 text-amber-800 cursor-pointer hover:bg-amber-200 border border-amber-300 shadow-sm hover:shadow-md'
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}
                    onClick={
                      canAcknowledge ? handleAcknowledgeClick : undefined
                    }
                    title={
                      canAcknowledge
                        ? tActivityCards.incident.clickToAcknowledge
                        : undefined
                    }>
                    {canAcknowledge ? (
                      <>
                        <CachedOutlinedIcon className="w-4 h-4" />
                        {tActivityCards.incident.clickToAcknowledgeBadge}
                      </>
                    ) : (
                      <>
                        <ReportProblemOutlinedIcon className="w-4 h-4" />
                        {tActivityCards.incident.incidentReported}
                      </>
                    )}
                  </span>
                </div>
                {incident?.incidentTime && (
                  <span className="text-gray-600 text-xs">
                    {tActivityCards.incident.timeOfIncident.replace(
                      '{time}',
                      formatActivityTime(incident.incidentTime),
                    )}
                  </span>
                )}
              </div>
              {incident?.delay && (
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">{tActivityCards.incident.delayLabel}</span>{' '}
                  {incident.delay} {getDelayUnitDisplay(incident.delayUnitOfMeasure)}
                </p>
              )}
              {incident?.incidentType && (
                <p className="text-gray-700">
                  <span className="font-medium text-gray-900">{tActivityCards.incident.typeLabel}</span>{' '}
                  {getIncidentTypeDisplay(incident.incidentType, incident.otherIncidentType)}
                </p>
              )}
              {incident?.status && (
                <div className="flex items-center justify-between">
                  <p className="text-gray-700">
                    <span className="font-medium text-gray-900">{tActivityCards.incident.statusLabel}</span>{' '}
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium  ${
                        incident.status === 'PENDING'
                          ? 'bg-yellow-400 text-yellow-900'
                          : 'bg-green-100 text-green-800'
                      }`}>
                      {incidentStatusTextMap[incident.status] || incident.status}
                    </span>
                  </p>
                  {canAcknowledge && (
                    <button type="button"
                      onClick={handleAcknowledgeClick}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-500 hover:bg-green-600 active:bg-green-700 rounded-md transition-all duration-200 shadow-sm hover:shadow-md">
                      <CheckCircleOutlinedIcon className="w-4 h-4" />
                      {tActivityCards.incident.acknowledgeButton}
                    </button>
                  )}
                </div>
              )}

              {canAcknowledge && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-blue-800 text-xs font-medium flex items-center gap-2">
                    <EmojiObjectsOutlinedIcon className="w-4 h-4 flex-shrink-0" />
                    <span>{tActivityCards.incident.needsAttentionHint}</span>
                  </p>
                </div>
              )}
            </div>
          </Alert>
        )}

        {incident?.description && (
          <Alert className="bg-gray-50 rounded-md p-3">
            <p className="text-gray-800 text-sm leading-relaxed">
              {incident.description}
            </p>
          </Alert>
        )}

        {/* Documents Section */}
        {incident?.documents && incident.documents.length > 0 && (
          <div className="mt-3">
            <p className="text-sm text-neutral-700 font-medium mb-2">
              {tActivityCards.incident.attachments.replace('{n}', String(incident.documents.length))}
            </p>
            <div className="flex flex-wrap gap-2">
              {incident.documents.map((document: any, index: number) => {
                const fileType = getFileType(document.name);
                const isImage = isImageFile(document.name);
                return (
                  <div
                    key={document.documentId || index}
                    onClick={(event) => {
                      // Sprint 16, develop run 2 (fix for regression found during review). This
                      // click sits inside the now-clickable card; without stopping propagation it
                      // both opens the document in a new tab and navigates the current tab to the
                      // incident detail page.
                      event.stopPropagation();
                      window.open(document.url, '_blank');
                    }}
                    className="relative cursor-pointer group rounded-lg p-2 hover:border-gray-300 hover:shadow-md transition-all">
                    {isImage ? (
                      <div className="w-20 h-20 rounded overflow-hidden bg-gray-100 mb-2 relative">
                        <img
                          src={document.url}
                          alt={document.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                          <ZoomInOutlinedIcon
                            fontSize="small"
                            className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded bg-gray-100 flex items-center justify-center mb-2">
                        {getFileIcon(document.name)}
                      </div>
                    )}
                    <div className="text-center">
                      <p
                        className="text-xs text-gray-600 truncate mb-1"
                        title={document.name}>
                        {document.name?.length > 12
                          ? `${document.name.substring(0, 12)}...`
                          : document.name || tActivityCards.incident.documentFallback}
                      </p>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getTypeColor(fileType)}`}>
                        {fileType}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
